import { CustomEventWithDetail, html, type Html, UnsafeHtml, View } from 'rune-ts';
import klass from './Carousel.module.scss';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

export interface slideOptions {
  width: number;
  img: string;
}

export interface CarouselData {
  slides: { id?: string | number; title?: string; is_selected: boolean; opt?: slideOptions }[];
  hideDisabledButtons: boolean;
  buttonIcon?: View | Html | UnsafeHtml;
  selectOnClick: boolean; // 슬라이드 클릭 시 선택 토글
  autoSlideOnSelect?: boolean; // 클릭 선택 시 해당 슬라이드로 이동
  options: object;
}

export class onSelectEvent extends CustomEventWithDetail<object> {}

export class Carousel extends View<CarouselData> {
  public swiper!: Swiper;

  setSlidesPerGroup(n: number) {
    if (!this.swiper) return;
    this.swiper.params.slidesPerGroup = n;
    this.swiper.update();
  }

  override template() {
    const { slides, hideDisabledButtons, buttonIcon } = this.data;

    return html`
      <div class="${klass.wrapper} ${hideDisabledButtons ? klass.hideDisabled : ''}">
        <div class="${klass.carousel} swiper">
          <div class="swiper-wrapper">
            ${slides.map(
              (item) => html`
                <div
                  class="swiper-slide ${item.is_selected ? klass.selected : ''}"
                  style="${item.opt?.width ? `width:${item.opt.width}px;` : ''}"
                >
                  ${item.opt?.img
                    ? html`<div class="${klass.image_wrapper}">
                        <img src="${item.opt.img}" alt="${item.title}" />
                      </div>`
                    : ``}
                  <div class="${klass.title}">${item.title}</div>
                </div>
              `,
            )}
          </div>
        </div>

        <div class="${klass.prev}">${buttonIcon}</div>
        <div class="${klass.next}">${buttonIcon}</div>
      </div>
    `;
  }

  private applySelection(idx: number) {
    const $carousel = this.element().querySelector(`.${klass.carousel}`) as HTMLElement;
    const $slides = Array.from($carousel.querySelectorAll('.swiper-slide')) as HTMLElement[];

    $slides.forEach((s, i) => {
      const isSel = i === idx;
      s.classList.toggle(klass.selected, isSel);
      s.setAttribute('aria-selected', String(isSel));
    });

    if (this.data.slides) {
      this.data.slides.forEach((it, i) => (it.is_selected = i === idx));
    }
  }

  private setupSelectionHandlers() {
    const $slides = Array.from(this.element().querySelectorAll('.swiper-slide')) as HTMLElement[];

    const selectOnClick = this.data.selectOnClick ?? true;
    const autoSlideOnSelect = this.data.autoSlideOnSelect ?? true;

    let selectedIdx = this.data.slides.findIndex((_, i) => this.data.slides?.[i]?.is_selected) ?? 0;

    if (selectedIdx < 0) selectedIdx = 0;
    this.applySelection(selectedIdx);

    if (!selectOnClick) return;

    $slides.forEach((el, i) => {
      el.addEventListener('click', (_e) => {
        if (selectedIdx === i) {
          this.dispatchEvent(onSelectEvent, { bubbles: true, detail: this.data.slides?.[i] ?? {} });
          return;
        }

        selectedIdx = i;
        this.applySelection(i);

        if (autoSlideOnSelect) {
          this.swiper.slideTo(i);
        }
        this.dispatchEvent(onSelectEvent, { bubbles: true, detail: this.data.slides?.[i] ?? {} });
      });
    });
    if (autoSlideOnSelect) {
      this.swiper.slideTo(selectedIdx);
    }
  }

  override onRender() {
    const $el = this.element() as HTMLElement;
    const $carousel = $el.querySelector(`.${klass.carousel}`) as HTMLElement;
    const initialSlide = this.data.slides.findIndex((_, i) => this.data.slides?.[i]?.is_selected) ?? 0;

    this.swiper = new Swiper($carousel, {
      modules: [Navigation, Pagination],
      slidesPerView: 'auto',
      allowTouchMove: false,
      initialSlide,
      navigation: {
        prevEl: $el.querySelector(`.${klass.prev}`) as HTMLElement,
        nextEl: $el.querySelector(`.${klass.next}`) as HTMLElement,
      },
      a11y: { enabled: true },
      ...this.data.options,
    });

    this.setupSelectionHandlers();
  }
}
